import React, { useEffect, useState } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import { useAppSelector } from "@/app/hooks";
import { fetchEventDetails } from "@/modules/eventRegistration/features/EventDirectory/api/fetchEventDetails/fetchEventDetailsApi";
import {
  EventDetails,
  SessionData,
} from "@/modules/eventRegistration/features/EventDirectory/api/fetchEventDetails/types";
import {
  formatDateRangeInTimeZone,
  formatTimeRangeInTimeZone,
} from "@/modules/eventRegistration/features/EventDirectory/utils/dateUtils";
import { buildLegacyEventUrl } from "@/modules/eventRegistration/utils/buildLegacyEventUrl";
import {
  isPreEpRegistrationStepSlug,
  PRE_EP_REGISTRATION_STEPS,
} from "../../config/preEpRegistrationSteps";
import { PRE_EP_HOME } from "../../config/preEpStage";
import { ArrowIcon, preEpCard } from "../PreEpUi/PreEpUi";
import { BackToSteps } from "../PreEpEventChoice/PreEpEventChoice";

const venueText = (session: SessionData): string => {
  if (session.venue) {
    const city = session.venue.city
      ? `${session.venue.city}, ${session.venue.state ?? ""}`.trim()
      : null;
    return [session.venue.name, city].filter(Boolean).join(" · ");
  }
  return session.isVirtualOnly ? "Online" : "";
};

/**
 * The sessions of one Pre-EP event. Picking one starts event checkout.
 */
const PreEpSessionChoice: React.FC = () => {
  const { step, eventUuid } = useParams<{ step: string; eventUuid: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Back returns to the event cards only if they came from them; with a
  // single event the cards are skipped, so Back goes to the steps.
  const fromEventChoice =
    (location.state as { fromEventChoice?: boolean } | null)
      ?.fromEventChoice === true;
  const memberPortalCompanyId = useAppSelector(
    (state) => state.userAppSettings.userAppSettings?.memberPortalCompanyId,
  );
  const [event, setEvent] = useState<EventDetails | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!eventUuid) return;
    fetchEventDetails({ uuid: eventUuid })
      .then((response) => setEvent(response.data))
      .finally(() => setLoading(false));
  }, [eventUuid]);

  if (!isPreEpRegistrationStepSlug(step) || !eventUuid) {
    return <Navigate replace to={PRE_EP_HOME} />;
  }
  if (loading) {
    return <LoadingIndicator />;
  }

  const sessions = (event?.sessions ?? []).filter((s) => !s.isCancelled);
  const register = (session: SessionData) => {
    if (session.redirectToLegacy) {
      window.open(
        buildLegacyEventUrl(
          "directory",
          event?.legacyCourseId ?? null,
          session.memberPortalRegistrationUrl,
          memberPortalCompanyId ?? null,
        ),
        "_blank",
        "noopener,noreferrer",
      );
      return;
    }
    navigate(`/event-registration/events/${session.uuid}/register`);
  };

  return (
    <div className="relative mx-auto flex max-w-[1000px] flex-col gap-8 px-5 pb-16 pt-8 md:px-11 md:pt-12">
      {fromEventChoice ? (
        <BackToSteps label="Back" to={`/pre-ep/register/${step}`} />
      ) : (
        <BackToSteps />
      )}
      <header className="flex flex-col gap-2">
        <h1 className="m-0 text-[30px] font-light md:text-[44px]">
          {PRE_EP_REGISTRATION_STEPS[step].sessionTitle}
        </h1>
        {event && (
          <p className="m-0 text-lg text-[#C3CBE0]">{event.eventName}</p>
        )}
      </header>
      {sessions.length === 0 ? (
        <p className={`${preEpCard} m-0 text-[15px] text-[#D3D9EA]`}>
          There are no upcoming dates right now. Check back soon, or reach out
          to your coach.
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-3.5 p-0">
          {sessions.map((session) => {
            const available = !session.isFinalized;
            const dates = formatDateRangeInTimeZone(
              session.startDate || null,
              session.endDate || null,
              session.timezoneIdentifier || null,
            );
            const times = formatTimeRangeInTimeZone(
              session.startDate || null,
              session.endDate || null,
              session.timezoneIdentifier || null,
            );
            const seats = session.availableSeats;
            return (
              <li key={session.uuid}>
                <button
                  className={`${preEpCard} flex w-full flex-wrap items-center gap-4 text-left text-white transition-colors ${available ? "cursor-pointer hover:border-[#5A6BA0] hover:bg-[#1B274B]" : "cursor-not-allowed opacity-60"}`}
                  disabled={!available}
                  onClick={() => register(session)}
                  type="button"
                >
                  <span className="flex min-w-0 flex-[1_1_320px] flex-col gap-1">
                    <span className="text-[20px] font-bold">{dates}</span>
                    <span className="text-[15px] text-[#D3D9EA]">
                      {times}
                      {session.timezoneShortName
                        ? ` (${session.timezoneShortName})`
                        : ""}
                    </span>
                    <span className="text-[15px] text-[#C3CBE0]">
                      {venueText(session)}
                    </span>
                  </span>
                  <span className="flex flex-col items-start gap-2 sm:items-end">
                    <span className="text-sm font-semibold text-[#C3CBE0]">
                      {!available
                        ? "Registration closed"
                        : seats > 0
                          ? `${seats} ${seats === 1 ? "seat" : "seats"} left`
                          : "Waitlist only"}
                    </span>
                    {available && (
                      <span className="inline-flex items-center gap-2 text-[15px] font-bold">
                        Choose this date <ArrowIcon />
                      </span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default PreEpSessionChoice;
